/** Zona 1 do leitor (tela L-02): navegação de painéis e progresso. */
import { useReaderStore } from '@/features/reader/stores/readerStore'
import { sectionForPage } from '@/features/reader/utils/toc'
import type { BookAccess } from '@/types/api'
import { PalavrasLogo } from '@/components/PalavrasLogo'
import { isPalavras } from '@/services/palavrasBrand'

interface TopBarProps {
  access: BookAccess
  onClose: () => void
}

export function TopBar({ access, onClose }: TopBarProps) {
  const page = useReaderStore((s) => s.page)
  const sidebarAberta = useReaderStore((s) => s.sidebarAberta)
  const painelDireito = useReaderStore((s) => s.painelDireito)
  const acessibilidadeAberta = useReaderStore((s) => s.acessibilidadeAberta)
  const alternarSidebar = useReaderStore((s) => s.alternarSidebar)
  const abrirPainel = useReaderStore((s) => s.abrirPainel)
  const alternarAcessibilidade = useReaderStore((s) => s.alternarAcessibilidade)

  const total = access.page_count ?? 0
  const secao = sectionForPage(access.toc, page)
  const pct = total > 0 ? Math.round((page / total) * 100) : 0

  return (
    <header className="reader-topbar">
      {isPalavras() && <span className="reader-publisher-logo"><PalavrasLogo /></span>}
      <button
        type="button"
        className="reader-topbar__menu"
        onClick={alternarSidebar}
        aria-expanded={sidebarAberta}
        aria-controls="reader-sidebar"
        aria-label={sidebarAberta ? 'Fechar o sumário' : 'Abrir o sumário'}
      >
        ☰
      </button>

      <button type="button" onClick={onClose} aria-label="Voltar para a biblioteca">
        ← Biblioteca
      </button>

      <h1 className="reader-topbar__titulo">
        {access.title}
        {secao && <span className="reader-topbar__secao"> — {secao.title}</span>}
      </h1>

      {/* Só o indicador é `live`: anunciar a barra inteira a cada virada de
          página deixaria o leitor de tela tagarela. */}
      <span className="reader-topbar__pagina" aria-live="polite">
        Pág. <span className="reader-topbar__numero">{page}</span>
        {total > 0 && `/${total}`}
      </span>

      {total > 0 && (
        <span
          className="reader-topbar__progresso"
          role="progressbar"
          aria-label="Progresso de leitura"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${pct}% do livro`}
        >
          {/* Largura por `style` inline: o React escreve pela CSSOM, que a CSP
              não cobre — a proibição do CLAUDE.md §11 é do painel HTMX, que é
              servido pelo backend com `default-src 'self'` sem `unsafe-inline`.
              Aqui isso permite granularidade de 1%, em vez de 21 classes. */}
          <span className="reader-topbar__barra" style={{ width: `${pct}%` }} />
        </span>
      )}

      <nav className="reader-topbar__acoes" aria-label="Painéis do leitor">
        <button
          type="button"
          onClick={() => abrirPainel('destaques')}
          aria-pressed={painelDireito === 'destaques'}
        >
          Anotações e destaques
        </button>
        {access.format === 'pdf' && <button
          type="button"
          onClick={() => abrirPainel('tutor')}
          aria-pressed={painelDireito === 'tutor'}
        >
          Tutor
        </button>}
        <button
          type="button"
          onClick={alternarAcessibilidade}
          aria-pressed={acessibilidadeAberta}
          aria-label="Configurações de leitura"
        >
          Aa
        </button>
      </nav>
    </header>
  )
}
